import { useCallback, useMemo, useState } from 'react';

import { TMusicLibrary } from './MusicLibrary';
import { TracksByCategory } from './types';

import { IOption } from '../MobileSelect';

import { useNonconcurrentPlay } from 'utils/audioPlayer/useNonconcurrentPlay';
import { Normalize } from 'utils/types';
import { richTextWithPlaceholder } from 'utils/prismic';

export const useMusicLibraryData = (data: TMusicLibrary) => {
  if (!('slice_type' in data)) return null;

  const { primary, items } = data;

  const [activeIndex, setActiveIndex] = useState(0);
  const { trackPlayerRefs, ...nonconcurrentPlayProps } = useNonconcurrentPlay();

  const { music_library_is_dark: isDark, music_library_title: title } = primary;

  const { tracksByCategory, ...tracksInfo } = useMemo(() => {
    let largestTrackName = '';
    let largestTrackGenres = '';

    const tracksByCategory: TracksByCategory[] = items.reduce(
      (acm, { music_library_category_name: name, music_library_category: category }) => {
        if (!name || !category || !category?.document) return acm;

        const { data } = category.document as Normalize<Queries.MusicLibraryCategoryFragment>;

        const tracks =
          data.tracks?.reduce((acm, { track, track_name, track_genres }) => {
            if (!track?.url) return acm;

            if (largestTrackName.length < track_name.length) largestTrackName = track_name;
            if (largestTrackGenres.length < track_genres.length) largestTrackGenres = track_genres;

            return [
              ...acm,
              {
                track: track.url,
                trackName: track_name ?? '',
                trackGenres: track_genres ?? '',
              },
            ];
          }, []) ?? [];

        return [...acm, { name, tracks }];
      },
      []
    );

    return { largestTrackName, largestTrackGenres, tracksByCategory };
  }, [items]);

  if (!tracksByCategory.length) return null;

  const composeCategoryClickHandle = index => () => {
    trackPlayerRefs.current = {};
    setActiveIndex(index);
  };

  const currentTracksCategory = useMemo(
    () => tracksByCategory[activeIndex],
    [activeIndex, tracksByCategory]
  );

  const options = useMemo(
    () =>
      tracksByCategory.map(({ name }, index) => ({
        index,
        label: name,
      })),
    [tracksByCategory]
  );

  const onChange = useCallback(({ index }: IOption) => {
    composeCategoryClickHandle(index)();
  }, []);

  const value = useMemo(() => options[activeIndex], [activeIndex]);

  return {
    isDark,
    title: richTextWithPlaceholder(title, ''),
    composeCategoryClickHandle,
    activeIndex,
    tracksByCategory,
    categoriesProps: {
      tracksByCategory,
      activeIndex,
      composeCategoryClickHandle,
      selectProps: { options, onChange, value },
    },
    tracksProps: {
      currentTracksCategory,
      ...tracksInfo,
      ...nonconcurrentPlayProps,
    },
  };
};
